import {
  getPeriodEndIso,
  getPeriodStartIso,
  isoToPeriod,
  isPureArray,
  type PeriodType,
} from '@asset-metrix/portal-ui';

import {
  FilterSelectionSlice,
  FilterSelectionValue,
} from '../store/states/filterSelection';
import {
  FilterConfigDto,
  isDateRangePickerDisplay,
} from '../store/states/filterTypes';
import {
  MIN_COMPARISON_ENTITY_COUNT,
  parseDelimitedParamValues,
} from '../utils/comparisonDetailPageEntities';

function snapPeriodRangeEndpoint(
  isoDate: string,
  type: PeriodType,
  endpoint: 'start' | 'end'
): string | undefined {
  const period = isoToPeriod(isoDate.slice(0, 10), type);
  if (!period) {
    return undefined;
  }
  return endpoint === 'start'
    ? getPeriodStartIso(period, type)
    : getPeriodEndIso(period, type);
}

type ParsedFilterValue = string[] | boolean[] | null;

const parseFilterValue = (filters: string[] | null): ParsedFilterValue => {
  if (!Array.isArray(filters)) {
    return filters;
  }

  const parsedFilters = filters.map((f) => {
    if (f === 'true') {
      return true;
    }

    if (f === 'false') {
      return false;
    }

    return f;
  });

  if (isPureArray(parsedFilters)) {
    return parsedFilters;
  }

  return filters;
};

function parseSelectionFromUrl(
  filterValueFromUrl: string[] | null,
  filterConfig: FilterConfigDto
): FilterSelectionValue | undefined {
  const value = parseFilterValue(filterValueFromUrl);
  if (value == null || value.length === 0) {
    return undefined;
  }

  if (filterConfig.display === 'multi-select') {
    return value;
  }

  if (filterConfig.display === 'comparison') {
    const normalized = parseDelimitedParamValues(value.map(String));
    return normalized.length === MIN_COMPARISON_ENTITY_COUNT
      ? normalized
      : undefined;
  }

  if (
    ['single-select', 'search', 'single-date-picker'].includes(
      filterConfig.display
    )
  ) {
    return value[0];
  }

  if (isDateRangePickerDisplay(filterConfig.display)) {
    try {
      const rangeObj: { gte: string; lte: string } = JSON.parse(
        String(value[0])
      );
      if (rangeObj.gte != null || rangeObj.lte != null) {
        const range = {
          ...(rangeObj.lte != null ? { end: rangeObj.lte } : {}),
          ...(rangeObj.gte != null ? { start: rangeObj.gte } : {}),
        };
        if (filterConfig.display === 'range-date-picker') {
          return range;
        }

        const type =
          filterConfig.display === 'month-range-picker' ? 'month' : 'quarter';
        const start =
          range.start != null
            ? snapPeriodRangeEndpoint(range.start, type, 'start')
            : undefined;
        const end =
          range.end != null
            ? snapPeriodRangeEndpoint(range.end, type, 'end')
            : undefined;
        if (start == null && end == null) {
          return undefined;
        }
        return {
          ...(start != null ? { start } : {}),
          ...(end != null ? { end } : {}),
        };
      }
    } catch {
      throw new Error(
        `Parsing search params for key "${filterConfig.key}" failed`
      );
    }
  }

  if (filterConfig.display === 'range-number-picker') {
    try {
      const rangeObj: { gte: string; lte: string } = JSON.parse(
        String(value[0])
      );
      if (rangeObj.gte != null || rangeObj.lte != null) {
        return {
          ...(rangeObj.lte != null
            ? { end: Number.parseFloat(rangeObj.lte) }
            : {}),
          ...(rangeObj.gte != null
            ? { start: Number.parseFloat(rangeObj.gte) }
            : {}),
        };
      }
    } catch {
      throw new Error(
        `Parsing search params for key "${filterConfig.key}" failed`
      );
    }
  }

  return undefined;
}

export const presetSelectionFromUrl = (
  presetFilterSelectionValue: FilterSelectionSlice['presetFilterSelectionValue'],
  instanceId: string,
  filterValueFromUrl: string[] | null,
  filterConfig: FilterConfigDto
) => {
  const selection = parseSelectionFromUrl(filterValueFromUrl, filterConfig);
  if (selection != null) {
    presetFilterSelectionValue(instanceId, filterConfig.key, selection);
  }
};
